import React, { useState, useEffect, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import {
  Menu,
  Bell,
  CircleHelp,
  ChevronDown,
  Check,
  CheckCheck,
  UserRound,
  LogOut,
} from 'lucide-react'
import { toggleSidebar, toggleSidebarMobile } from '../../store/slices/uiSlice'
import { useAuth } from '../../contexts/AuthContext'
import { toast } from 'react-toastify'
import { useNavigate } from 'react-router-dom'
import notificationService from '../../services/notificationService'
import './Header.css'

const alertReasonLabel = (meta = {}) => {
  const map = {
    renewal: 'Renewal',
    expiry: 'Expiry',
    payment: 'Payment',
    cost_overrun: 'Cost overrun',
    rental_return: 'Rental return',
    notice: 'Notice',
    other: 'Alert',
  }
  return map[meta.alertReason] || (meta.module === 'subscriptions' ? 'Subscription' : 'Notification')
}

/** Map stored actionUrl (incl. legacy /task/:id) to a real app route */
const resolveNotificationUrl = (n) => {
  const raw = (n?.actionUrl || '').trim()
  if (raw) {
    const taskLegacy = raw.match(/^\/task\/([a-fA-F0-9]{24})\/?$/)
    if (taskLegacy) return `/projects/tasks/view/${taskLegacy[1]}`
    if (raw.startsWith('/')) return raw
  }
  const entityId = n?.relatedEntityId || n?.metadata?.taskId || n?.metadata?.subscriptionId
  if (!entityId) return null
  const type = n?.relatedEntityType || n?.type
  if (type === 'ActionItem' || type === 'task') return `/projects/tasks/view/${entityId}`
  if (n?.metadata?.module === 'subscriptions' || type === 'alert') {
    const subId = n?.metadata?.subscriptionId || entityId
    return `/subscriptions/view/${subId}`
  }
  return null
}

const Header = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user, logout } = useAuth()

  const { currentCompany } = useSelector((state) => state.company)

  const [showUserDropdown, setShowUserDropdown] = useState(false)
  const [showNotifDropdown, setShowNotifDropdown] = useState(false)
  const [notifications, setNotifications] = useState([])
  const [unreadCount, setUnreadCount] = useState(0)
  const dropdownRef = useRef(null)
  const notifRef = useRef(null)

  const loadNotifications = async () => {
    try {
      const [countRes, listRes] = await Promise.all([
        notificationService.getUnreadCount(),
        notificationService.getAll({ limit: 15 }),
      ])
      if (countRes.success) {
        setUnreadCount(countRes.data?.unreadCount ?? countRes.unreadCount ?? 0)
      }
      if (listRes.success) {
        const rows = listRes.data?.notifications || listRes.notifications || listRes.data || []
        setNotifications(Array.isArray(rows) ? rows : [])
      }
    } catch (err) {
      // silent — bell remains available
    }
  }

  useEffect(() => {
    loadNotifications()
    const interval = setInterval(loadNotifications, 30000)
    return () => clearInterval(interval)
  }, [])

  const handleLogout = async () => {
    try {
      await logout()
      toast.success('You have been logged out successfully.', { autoClose: 2000 })
      navigate('/login')
    } catch (error) {
      console.error('Logout error:', error)
      navigate('/login')
    }
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowUserDropdown(false)
      }
      if (notifRef.current && !notifRef.current.contains(event.target)) {
        setShowNotifDropdown(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleMenuToggle = () => {
    if (typeof window !== 'undefined' && window.matchMedia('(max-width: 768px)').matches) {
      dispatch(toggleSidebarMobile())
    } else {
      dispatch(toggleSidebar())
    }
  }

  const openNotification = async (n) => {
    try {
      if (!n.read && (n.id || n._id)) {
        await notificationService.markAsRead(n.id || n._id)
        setUnreadCount((c) => Math.max(0, c - 1))
        setNotifications((prev) => prev.map((x) =>
          (x.id || x._id) === (n.id || n._id) ? { ...x, read: true } : x))
      }
    } catch (_) { /* ignore */ }
    setShowNotifDropdown(false)
    const url = resolveNotificationUrl(n)
    if (url) navigate(url)
  }

  const markAllRead = async (e) => {
    e?.stopPropagation?.()
    e?.preventDefault?.()
    if (unreadCount <= 0 && notifications.every((n) => n.read)) {
      toast.info('No unread notifications')
      return
    }
    try {
      const r = await notificationService.markAllAsRead()
      if (r?.success === false) {
        toast.error(r?.message || 'Failed to mark all as read')
        return
      }
      setUnreadCount(0)
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
      toast.success('All notifications marked as read')
    } catch (_) {
      toast.error('Failed to mark all as read')
    }
  }

  const formatWhen = (value) => {
    if (!value) return ''
    try {
      return new Date(value).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
      })
    } catch {
      return ''
    }
  }

  return (
    <header className="header">
      <div className="header-left">
        <button type="button" className="menu-toggle" onClick={handleMenuToggle} aria-label="Menu">
          <Menu size={20} />
        </button>
        <div className="brand-section">
          <div className="brand-text">
            <div className="brand-name">{currentCompany?.companyName || 'Celeris Ventures - AI-Powered ERP'}</div>
            {currentCompany?.companyCode && (
              <div className="company-code">{currentCompany.companyCode}</div>
            )}
          </div>
        </div>
      </div>

      <div className="header-right">
        <div className="notification-wrap" ref={notifRef}>
          <button
            type="button"
            className="notification-btn"
            aria-label="Notifications"
            onClick={() => {
              setShowNotifDropdown((v) => !v)
              if (!showNotifDropdown) loadNotifications()
            }}
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span className="notification-badge">{unreadCount > 99 ? '99+' : unreadCount}</span>
            )}
          </button>
          {showNotifDropdown && (
            <div className="notification-dropdown">
              <div className="notification-dropdown-header">
                <strong>Notifications</strong>
                <button
                  type="button"
                  className="notif-mark-all"
                  onClick={markAllRead}
                  title="Mark all as read"
                  disabled={unreadCount <= 0}
                >
                  <CheckCheck size={15} />
                  <span>Mark all read</span>
                </button>
              </div>
              <div className="notification-dropdown-list">
                {notifications.length === 0 && (
                  <div className="notification-empty">No notifications</div>
                )}
                {notifications.map((n) => (
                  <button
                    type="button"
                    key={n.id || n._id}
                    className={`notification-item ${n.read ? 'read' : 'unread'}`}
                    onClick={() => openNotification(n)}
                  >
                    <div className="notification-item-top">
                      <span className="notification-reason">{alertReasonLabel(n.metadata)}</span>
                      <span className="notification-time">{formatWhen(n.createdAt)}</span>
                    </div>
                    <div className="notification-title">{n.title}</div>
                    <div className="notification-message">{n.message}</div>
                    {!n.read && <Check size={12} className="notification-unread-dot" />}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <button
          type="button"
          className="header-btn"
          aria-label="Help"
          onClick={() => navigate('/support')}
        >
          <CircleHelp size={18} />
        </button>

        <div className="user-profile" ref={dropdownRef}>
          <button
            className="user-btn"
            onClick={() => setShowUserDropdown(!showUserDropdown)}
            type="button"
          >
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <ChevronDown size={14} />
          </button>

          {showUserDropdown && (
            <div className="account-menu" role="menu">
              <div className="account-menu-head">
                <div className="user-avatar-large">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                <div className="account-menu-id">
                  <div className="account-menu-name">{user?.name || 'User'}</div>
                  <div className="account-menu-mail">{user?.userEmail || user?.email || '—'}</div>
                  {user?.userCode && <div className="account-menu-code">{user.userCode}</div>}
                </div>
              </div>
              <button
                type="button"
                className="account-menu-item"
                role="menuitem"
                onClick={() => {
                  setShowUserDropdown(false)
                  navigate('/profile')
                }}
              >
                <UserRound size={16} />
                My profile
              </button>
              <button type="button" className="account-menu-item account-menu-logout" role="menuitem" onClick={handleLogout}>
                <LogOut size={16} />
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  )
}

export default Header
